import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Link as LinkIcon,
  Copy,
  Loader2,
  Check,
  AlertTriangle,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import comparativesApi from "@/api/comparatives";

/**
 * Phase 5.6 — Share-link modal for a saved comparative.
 *
 * - Toggle ON  → POST /api/comparatives/:id/share → token returned.
 * - Toggle OFF → DELETE /api/comparatives/:id/share → marks revoked.
 * - Re-toggle ON → POST same endpoint → idempotent: reuses existing token.
 *
 * The public URL is `${window.location.origin}/public/comparatives/:token`.
 */
export default function ShareComparativeDialog({ open, comparative, onClose, onChanged }) {
  const [doc, setDoc] = useState(comparative);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setDoc(comparative);
    setCopied(false);
  }, [comparative, open]);

  if (!doc) return null;

  const enabled = !!doc.share_token && !doc.share_revoked;
  const publicUrl = enabled
    ? `${window.location.origin}/public/comparatives/${doc.share_token}`
    : "";

  const onToggle = async (next) => {
    setBusy(true);
    try {
      if (next) {
        const r = await comparativesApi.share(doc.id);
        setDoc(r.data);
        onChanged?.(r.data);
        toast.success("Enlace público activado");
      } else {
        const r = await comparativesApi.revokeShare(doc.id);
        setDoc(r.data);
        onChanged?.(r.data);
        toast.success("Enlace revocado");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error actualizando enlace";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Enlace copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar — copia el enlace manualmente");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="share-comparative-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="h-4 w-4 text-primary" />
            Compartir comparativo
          </DialogTitle>
          <DialogDescription>
            Genera un enlace público de solo lectura para «{doc.name}». Cualquiera con el enlace podrá ver la gráfica.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 p-3">
            <div>
              <div className="text-sm font-medium">Habilitar enlace público</div>
              <div className="text-[11px] text-muted-foreground">
                {enabled
                  ? "Cualquiera con el enlace puede ver el comparativo."
                  : "Activa para generar un enlace compartible."}
              </div>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={onToggle}
              disabled={busy}
              data-testid="share-toggle"
            />
          </div>

          {busy && (
            <div className="flex items-center justify-center py-2 text-xs text-muted-foreground">
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              Procesando…
            </div>
          )}

          {enabled && publicUrl && !busy && (
            <>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Enlace público</label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={publicUrl}
                    data-testid="share-url-input"
                    onFocus={(e) => e.target.select()}
                    className="flex-1 rounded-md border border-border bg-background px-3 py-2 font-mono text-[11px]"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onCopy}
                    data-testid="share-copy-btn"
                    className="shrink-0"
                  >
                    {copied ? (
                      <Check className="mr-1 h-3 w-3 text-emerald-500" />
                    ) : (
                      <Copy className="mr-1 h-3 w-3" />
                    )}
                    {copied ? "Copiado" : "Copiar"}
                  </Button>
                </div>
              </div>
              <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-muted-foreground">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                <span>
                  Cualquiera con este enlace verá los datos del comparativo. Revócalo cuando ya no lo necesites.
                </span>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-row justify-between gap-2 sm:flex-row sm:justify-between">
          {enabled ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onToggle(false)}
              disabled={busy}
              data-testid="share-revoke-btn"
              className="text-red-500 hover:bg-red-500/10 hover:text-red-600"
            >
              <Trash2 className="mr-1 h-3 w-3" />
              Revocar enlace
            </Button>
          ) : (
            <span />
          )}
          <Button onClick={onClose} variant="default" size="sm" data-testid="share-close-btn">
            Listo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
